// API Security — GraphQL controls (v2 MUI).
//
// Three tabs: Policy / Operations / Audit log.
//
// All numeric surfaces (hero cards, per-limit meters, operations
// table, audit tail) are driven by the CP `/graphql/observability`
// endpoint, which aggregates against `layerd-v2-graphql-events` in
// ClickHouse. Zero-traffic sites render explicit "no traffic in
// window" empty states instead of hardcoded demo numbers.
//
// Backend wiring:
//   asec.gqlPolicy / gqlPolicySet / gqlPersistedList / gqlPersistedCreate
//   asec.gqlPersistedDelete / gqlPolicyTest / gqlObservability

import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Stack, Box, Typography, Tabs, Tab, Table, TableBody, TableCell, TableHead, TableRow,
  CircularProgress, LinearProgress, Tooltip, Checkbox,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Play, Shield, ShieldCheck, ShieldOff, Activity, AlertTriangle,
  Layers3, Gauge, Route, SearchCode, SquarePen, Eye, Hash, FileText, Terminal, Info,
  Lightbulb, Braces, Fingerprint, ScanLine, Binary, List, SlidersHorizontal,
  Copy, ListTree, Users,
} from "lucide-react";

import asec from "../../../../../utils/axios/v1/asecAxios";
import { useNotification } from "../../../../../contexts/NotificationContext";
import { formatDate } from "../../../../../utils/format";
import colors from "../../../../../utils/colors";
import typography from "../../../../../theme/typography";
import LayerdIcon from "../../../../common/LayerdIcon";
import { PrimaryButton, GhostButton } from "../../../../common/Button";
import { GreenSwitch } from "../../../../common/Greens";
import StickySaveBar from "../../../../common/StickySaveBar";
import { arraysEqual, collectObjectDiff, enabledLabel } from "../../../../common/stickySaveBarUtils";
import AsecPage from "./AsecPage";
import useAsecPermissions from "./useAsecPermissions";
import {
  asecTabsSx, asecFieldLabelSx,
  asecFormGhostButtonSx, asecFormPrimaryButtonSx,
  asecDialogActionsSx, asecIconButtonSx,
  AsecTextField, AsecField,
} from "./asecUi";
import {
  AsecMetricStrip, AsecDeltaSub, AsecStatusPill, AsecMono, AsecSectionCard, AsecHeroModeValue, AsecHeroStatValue,
} from "./components/asecPrimitives";
import {
  AsecEventsTableShell,
  AsecEventsHeadCell,
  AsecEventsBodyText,
  AsecEventsMonoLine,
  AsecEventsViewCell,
  AsecEventsTableFooter,
  asecEventRowSx,
} from "./components/AsecEventsTable";
import { AsecEventsFilterBar } from "./components/AsecEventsFilterBar";

// Enforcement modes recognised by the CP (`VALID_MODE`) and pingora
// (`Mode::from_db`). Anything not in this list is silently coerced
// to "off" — do not add options here without wiring them backend-side.
const MODES = [
  { value: "log_only", label: "Log only", icon: Eye, tone: "warn", desc: "Violations are recorded but the operation still reaches origin. Best for tuning limits before you commit." },
  { value: "enforce", label: "Reject (400)", icon: ShieldOff, tone: "danger", desc: "Returns a GraphQL-formatted error response with a list of violated limits. The operation never reaches origin." },
];

/** Body / help copy — 25px line height keeps multi-line descriptions readable (LDCP-214). */
const GQL_BODY_SX = { fontSize: 12, color: colors.neutral[700], lineHeight: "25px" };
const GQL_MUTED_SX = { fontSize: 12, color: colors.neutral[600], lineHeight: "25px" };
const GQL_CAPTION_SX = { fontSize: 12, color: colors.neutral[700], lineHeight: "25px" };

/** Page-wide readable line height for body copy (LDCP-214). */
const GQL_PAGE_TEXT_SX = {
  "& .MuiTypography-root": { lineHeight: "25px" },
};

// Per-limit UI metadata. `key` matches the API config field so the
// live `observability.limits[key]` blob feeds the meter directly.
const QUERY_LIMITS = [
  { key: "query_max_depth",       label: "Max depth",       icon: Layers3,  min: 1, max: 50,     def: 10 },
  { key: "query_max_complexity",  label: "Max complexity",  icon: Gauge,    min: 1, max: 100000, def: 1000 },
  { key: "query_max_aliases",     label: "Max aliases",     icon: Copy,     min: 0, max: 1000,   def: 15 },
  { key: "query_max_root_fields", label: "Max root fields", icon: ListTree, min: 0, max: 200,    def: 15 },
];
const MUTATION_LIMITS = [
  { key: "mutation_max_depth",      label: "Max depth",      icon: Layers3, min: 1, max: 50,     def: 5 },
  { key: "mutation_max_complexity", label: "Max complexity", icon: Gauge,   min: 1, max: 100000, def: 500 },
];

const GQL_POLICY_DIFF_FIELDS = [
  { key: "mode", label: "Enforcement mode", format: (v) => MODES.find((m) => m.value === v)?.label ?? String(v ?? "—") },
  {
    key: "graphql_routes",
    label: "GraphQL routes",
    format: (v) => (Array.isArray(v) ? v : []).join(", ") || "—",
    equal: arraysEqual,
  },
  { key: "allow_introspection", label: "Allow introspection", format: enabledLabel },
  { key: "require_persisted", label: "Require persisted queries", format: enabledLabel },
  { key: "query_max_depth", label: "Query max depth" },
  { key: "query_max_complexity", label: "Query max complexity" },
  { key: "query_max_aliases", label: "Query max aliases" },
  { key: "query_max_root_fields", label: "Query max root fields" },
  { key: "mutation_max_depth", label: "Mutation max depth" },
  { key: "mutation_max_complexity", label: "Mutation max complexity" },
  { key: "body_size_cap_bytes", label: "Body cap", format: (v) => `${v} bytes` },
];

// Human-readable large-number formatting for hero tiles.
const fmt = (n) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  if (v >= 1e9) return `${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return v.toLocaleString();
  return String(v);
};

const OP_KIND_FROM_TYPE = (t) => (t === "mutation" ? "m" : t === "subscription" ? "s" : "q");

// Time delta helper for the audit "since" column. Called client-side
// so it reflects wall-clock at render time without server round-trip.
function timeSince(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "";
  const diff = Math.max(0, Date.now() - then);
  if (diff < 60_000) return `${Math.floor(diff / 1000)} sec ago`;
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} min ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)} h ago`;
  return `${Math.floor(diff / 86_400_000)} d ago`;
}

// CSV export for the Audit tab. Trigger a browser download on the
// current live audit rows — no server round-trip; the payload is
// already client-side. Fields chosen to match what the Reason chip
// exposes in the UI so a CSV recipient sees the same story as the
// operator viewing the page.
function downloadAuditCsv(audit) {
  const header = [
    "ts",
    "operation_type",
    "operation_name",
    "path",
    "operation_hash",
    "src_ip",
    "failure_kind",
    "apollo_code",
    "depth",
    "complexity",
    "aliases",
    "root_fields",
    "result",
  ];
  const escape = (v) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const rows = (audit || []).map((r) => [
    r.ts, r.operation_type, r.operation_name, r.path, r.operation_hash,
    r.src_ip, r.failure_kind, r.apollo_code,
    r.depth, r.complexity, r.aliases, r.root_fields, r.result,
  ].map(escape).join(","));
  const csv = [header.join(","), ...rows].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const stamp = new Date().toISOString().replace(/[:.]/g, "-");
  a.href = url;
  a.download = `graphql-audit-${stamp}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function timeOfDay(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const h = String(d.getHours()).padStart(2, "0");
  const m = String(d.getMinutes()).padStart(2, "0");
  const s = String(d.getSeconds()).padStart(2, "0");
  return `${h}:${m}:${s}`;
}

// Map CH failure_kind to a UI "reason" bucket + icon. Kept flexible
// so a new failure_kind lands with a generic icon rather than a crash.
function failureBucket(k) {
  switch (k) {
    case "depth_exceeded":       return { key: "depth", icon: Layers3, tone: "danger" };
    case "complexity_exceeded":  return { key: "complexity", icon: Gauge, tone: "warn" };
    case "aliases_exceeded":     return { key: "aliases", icon: Copy, tone: "warn" };
    case "root_fields_exceeded": return { key: "root_fields", icon: ListTree, tone: "warn" };
    case "body_cap":
    case "body_size_exceeded":   return { key: "body_cap", icon: FileText, tone: "danger" };
    case "introspection":        return { key: "introspection", icon: Eye, tone: "warn" };
    case "not_persisted":        return { key: "persisted", icon: Hash, tone: "warn" };
    case "malformed":            return { key: "malformed", icon: AlertTriangle, tone: "danger" };
    default:                     return { key: k || "other", icon: AlertTriangle, tone: "warn" };
  }
}

function buildHeroMetrics(policy, obs) {
  const mode = policy.mode || "off";
  const modeLabel = mode === "log_only" ? "Log only" : mode === "enforce" ? "Reject (400)" : "Off";
  const modeTone = mode === "enforce" ? { kind: "danger", lbl: "Enforcing" }
    : mode === "log_only" ? { kind: "warn", lbl: "Observing" }
      : { kind: "default", lbl: "Off" };
  const routes = Array.isArray(policy.graphql_routes) ? policy.graphql_routes : ["/graphql"];
  const introspect = !!policy.allow_introspection;
  const persisted = !!policy.require_persisted;

  const hero = obs?.hero || {};
  const depthCap = policy.query_max_depth ?? 10;
  const noTraffic = !obs || !Number(hero.ops);

  return [
    {
      key: "mode",
      icon: Shield,
      highlight: true,
      label: "Active mode",
      value: (
        <AsecHeroModeValue label={modeLabel} pillKind={modeTone.kind} pillLabel={modeTone.lbl} />
      ),
      sub: <>Route · <strong>{routes[0] || "/graphql"}</strong> · introspection {introspect ? "on" : "off"} · persisted {persisted ? "on" : "off"}</>,
    },
    {
      key: "ops",
      icon: Activity,
      label: "Operations · 24h",
      value: noTraffic ? "—" : fmt(hero.ops),
      sub: hero.ops_delta_pct === null || hero.ops_delta_pct === undefined
        ? <Typography sx={GQL_MUTED_SX}>no prior window</Typography>
        : <AsecDeltaSub deltaPct={Number(hero.ops_delta_pct)} />,
    },
    {
      key: "block",
      icon: AlertTriangle,
      label: "Would block",
      value: noTraffic ? "—" : fmt(hero.blocks),
      valueSx: hero.blocks > 0 ? { color: "#7a5b10" } : undefined,
      sub: noTraffic
        ? <Typography sx={GQL_MUTED_SX}>no traffic in window</Typography>
        : <><strong style={{ color: "#7a5b10" }}>{Number(hero.would_block_pct || 0).toFixed(3)}%</strong> · {hero.distinct_callers_over_cap || 0} distinct callers</>,
    },
    {
      key: "depth",
      icon: Layers3,
      label: "Deepest seen",
      value: noTraffic
        ? <AsecHeroStatValue primary="-" secondary={`/ ${depthCap} limit`} />
        : <AsecHeroStatValue primary={hero.peak_depth ?? 0} secondary={`/ ${depthCap} limit`} />,
      sub: hero.deepest_over_last_hour
        ? <Typography component="span" sx={{ color: "#A11228" }}><strong>{hero.deepest_over_last_hour} ops</strong> over cap last hour</Typography>
        : <Typography sx={GQL_MUTED_SX}>{noTraffic ? "no traffic in window" : "no over-cap ops in last hour"}</Typography>,
    },
    {
      key: "complexity",
      icon: Gauge,
      label: "Top complexity",
      value: noTraffic ? "—" : fmt(hero.peak_complexity),
      sub: hero.top_complexity_op_name
        ? <AsecMono>{hero.top_complexity_op_name}</AsecMono>
        : <Typography sx={GQL_MUTED_SX}>{noTraffic ? "no traffic in window" : "no named operation"}</Typography>,
    },
  ];
}

// LimitField reads its live meter data from `obs.limits[spec.key]`.
// Falls back to a neutral empty meter when we have no observability.
function LimitField({ spec, meter, value, onChange, disabled }) {
  const m = meter || { p95: null, peak: null, pct: 0, tone: undefined };
  const overCap = m.tone === "warn";
  const palette = overCap
    ? { border: "#FFE89A", bg: "#FFFCEF", fill: "linear-gradient(90deg,#F2B429,#C29021)" }
    : { border: colors.neutral[200], bg: "#FAFAFB", fill: `linear-gradient(90deg,${colors.primary[300]},${colors.primary[500]})` };
  return (
    <Box sx={{
      display: "flex", flexDirection: "column", gap: 1, p: 1.75,
      border: `1px solid ${palette.border}`, borderRadius: "10px", backgroundColor: palette.bg,
    }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ fontSize: 14, fontWeight: 600 }}>
          <LayerdIcon icon={spec.icon} size="xs" color="neutral" />
          <Typography component="span" sx={{ fontSize: 14, fontWeight: 600 }}>{spec.label}</Typography>
        </Stack>
        <AsecTextField type="number" disabled={disabled} compact
          inputProps={{ min: spec.min, max: spec.max, style: { textAlign: "right", fontWeight: 600 } }}
          value={value ?? spec.def}
          onChange={(e) => onChange(parseInt(e.target.value, 10) || spec.def)}
          sx={{ width: 90 }} />
      </Stack>
      <Box sx={{ height: 8, borderRadius: "4px", backgroundColor: "#fff", border: `1px solid ${colors.neutral[200]}`, position: "relative", overflow: "hidden" }}>
        <Box sx={{
          position: "absolute", left: 0, top: 0, height: "100%",
          width: `${Math.min(Number(m.pct) || 0, 100)}%`, borderRadius: "3px", background: palette.fill,
        }} />
      </Box>
      <Stack direction="row" justifyContent="space-between" sx={{ ...GQL_MUTED_SX }}>
        <span>P95: <strong style={{ color: colors.neutral[1000] }}>{m.p95 === null || m.p95 === undefined ? "—" : m.p95}</strong></span>
        <span style={overCap ? { color: colors.yellow[200], fontWeight: 600 } : undefined}>
          peak: {m.peak === null || m.peak === undefined ? "—" : m.peak}{overCap ? " ↑ over cap" : ""}
        </span>
      </Stack>
    </Box>
  );
}

function ToggleRow({ icon, name, desc, on, onChange, disabled, right }) {
  return (
    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.5}
      sx={{ py: 1.75, borderBottom: `1px solid ${colors.neutral[100]}` }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ fontSize: 14, fontWeight: 600, mb: 0.375 }}>
          <LayerdIcon icon={icon} size="xs" color="neutral" />{name}
        </Stack>
        <Typography sx={GQL_BODY_SX}>{desc}</Typography>
      </Box>
      {right || <GreenSwitch checked={on} onChange={onChange} disabled={disabled} />}
    </Stack>
  );
}

function ModeHelpCard({ mode }) {
  return (
    <AsecSectionCard title="What each mode does" icon={Info} radius="12px" noPadding bodySx={{ p: 0 }}>
      {MODES.map((m) => {
        const current = mode === m.value;
        const ic = m.tone === "danger" ? { bg: "#FEECEE", fg: "#A11228" }
          : { bg: "#FFF6D0", fg: "#7a5b10" };
        return (
          <Stack key={m.value} direction="row" spacing={1.25} alignItems="flex-start"
            sx={{ px: 2, py: 2, borderBottom: `1px solid ${colors.neutral[100]}` }}>
            <Box sx={{
              width: 30, height: 30, borderRadius: "8px", display: "flex",
              alignItems: "center", justifyContent: "center", flexShrink: 0,
              backgroundColor: ic.bg, color: ic.fg,
            }}><LayerdIcon icon={m.icon} size="sm" /></Box>
            <Box>
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ fontSize: 14, fontWeight: 600, mb: 0.5 }}>
                {m.label} {current && <AsecStatusPill kind="warn" sx={{ fontSize: 12, height: 18 }}>current</AsecStatusPill>}
              </Stack>
              <Typography sx={GQL_BODY_SX}>{m.desc}</Typography>
            </Box>
          </Stack>
        );
      })}
    </AsecSectionCard>
  );
}

// TipCard now reads live p95 depth. Only renders a headroom
// suggestion when we have enough traffic (>=100 ops) to make a
// statistical claim; otherwise it just points at "Test query".
function TipCard({ policy, obs }) {
  const cap = policy.query_max_depth ?? 10;
  const p95Depth = obs?.limits?.query_max_depth?.p95;
  const ops = obs?.hero?.ops ?? 0;
  const overCap = obs?.hero?.deepest_over_last_hour ?? 0;
  const enoughData = Number(ops) >= 100 && p95Depth !== null && p95Depth !== undefined;
  const suggest = enoughData ? Math.max(2, Math.round(Number(p95Depth) * 1.5)) : null;
  return (
    <Box sx={{
      display: "flex", alignItems: "flex-start", gap: 1.5, p: "14px 16px",
      borderRadius: "10px", backgroundColor: "rgba(99,113,247,0.06)",
      border: "1px solid rgba(99,113,247,0.20)",
    }}>
      <Box sx={{
        width: 30, height: 30, borderRadius: "8px", backgroundColor: "#fff",
        border: "1px solid rgba(99,113,247,0.20)", color: colors.primary[300],
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
      }}><LayerdIcon icon={Lightbulb} size="sm" color="primary" /></Box>
      <Box>
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: colors.primary[500], mb: 0.5 }}>Tune from real traffic</Typography>
        {enoughData ? (
          <Typography sx={GQL_BODY_SX}>
            P95 query depth is <strong>{p95Depth}</strong> — a depth cap of <strong>{suggest}</strong> would cover typical traffic with 50% headroom{overCap > 0 ? ` while flagging ${overCap} abusive operation${overCap === 1 ? "" : "s"} in the last hour` : ""}.
            {cap !== suggest && (
              <Typography component="span" sx={{ display: "block", mt: 0.5, color: colors.primary[500], fontWeight: 500 }}>
                Currently at {cap}.
              </Typography>
            )}
          </Typography>
        ) : (
          <Typography sx={GQL_BODY_SX}>
            {Number(ops) === 0
              ? "No GraphQL traffic recorded in the last 24 hours. Use the Test query button in the page header to try a query against the current policy."
              : "Not enough traffic yet to suggest a tuned cap. Recommendations appear after 100 operations."}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function PolicyTab({ policyDraft, patch, canConfigure, obs }) {
  const meters = obs?.limits || {};
  const navigate = useNavigate();
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 2.5, alignItems: "start" }}>
      <Stack spacing={2.5}>
        <AsecSectionCard title="Mode & routes" icon={Route}
          right={<Typography sx={GQL_CAPTION_SX}>applies to all sites in this project</Typography>}
          radius="12px">
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1.5 }}>
            {[{ value: "off", label: "Off", icon: Shield, tone: "default", desc: "GraphQL parsing skipped entirely. Pass-through to origin." }, ...MODES].map((m) => {
              const selected = (policyDraft.mode || "off") === m.value;
              const pal = m.tone === "danger" ? { border: "#FBC9CF", bg: "#FFF7F8", fg: "#A11228" }
                : m.tone === "warn" ? { border: "#FFE89A", bg: "#FFFCEF", fg: "#7a5b10" }
                  : { border: colors.neutral[200], bg: "#FAFAFB", fg: colors.neutral[1000] };
              return (
                <Box key={m.value}
                  onClick={() => canConfigure && patch({ mode: m.value })}
                  sx={{
                    cursor: canConfigure ? "pointer" : "not-allowed",
                    borderRadius: "10px",
                    border: `1px solid ${selected ? colors.primary[300] : pal.border}`,
                    backgroundColor: selected ? "rgba(99,113,247,0.06)" : pal.bg,
                    p: 1.75, opacity: canConfigure ? 1 : 0.6,
                  }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                    <LayerdIcon icon={m.icon} size="xs" color={selected ? "primary" : "neutral"} />
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: pal.fg }}>{m.label}</Typography>
                  </Stack>
                  <Typography sx={GQL_BODY_SX}>{m.desc}</Typography>
                </Box>
              );
            })}
          </Box>

          <Box sx={{ mt: 2 }}>
            <Typography sx={{ ...asecFieldLabelSx, mb: 0.5 }}>GraphQL route patterns</Typography>
            <AsecTextField fullWidth disabled={!canConfigure}
              placeholder="/graphql, /api/graphql"
              value={(policyDraft.graphql_routes || ["/graphql"]).join(", ")}
              onChange={(e) => patch({ graphql_routes: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
              sx={{ "& .MuiInputBase-input": { fontSize: 12, lineHeight: "18px" } }} />
            <Typography sx={{ ...GQL_MUTED_SX, mt: 0.5 }}>
              Only requests whose URL matches one of these patterns are parsed and checked. Everything else passes through untouched.
            </Typography>
          </Box>
        </AsecSectionCard>

        <AsecSectionCard title="Query limits" icon={SearchCode}
          right={<Typography sx={GQL_CAPTION_SX}>read-side operations</Typography>} radius="12px">
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: "12px 16px" }}>
            {QUERY_LIMITS.map((spec) => (
              <LimitField key={spec.key} spec={spec} meter={meters[spec.key]}
                value={policyDraft[spec.key]}
                disabled={!canConfigure} onChange={(v) => patch({ [spec.key]: v })} />
            ))}
          </Box>
        </AsecSectionCard>

        <AsecSectionCard title="Mutation limits" icon={SquarePen}
          right={<Typography sx={GQL_CAPTION_SX}>write-side operations · usually tighter</Typography>} radius="12px">
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: "12px 16px" }}>
            {MUTATION_LIMITS.map((spec) => (
              <LimitField key={spec.key} spec={spec} meter={meters[spec.key]}
                value={policyDraft[spec.key]}
                disabled={!canConfigure} onChange={(v) => patch({ [spec.key]: v })} />
            ))}
          </Box>
        </AsecSectionCard>

        <AsecSectionCard title="Schema & payload" icon={ShieldCheck} radius="12px" bodySx={{ pt: 0 }}>
          <ToggleRow
            icon={Eye} name="Allow introspection"
            desc={<>When off, the gateway returns <AsecMono>403</AsecMono> for any <AsecMono>__schema</AsecMono> or <AsecMono>__type</AsecMono> meta-field. Turn this off in production to hide your schema from scrapers.</>}
            on={!!policyDraft.allow_introspection} disabled={!canConfigure}
            onChange={() => patch({ allow_introspection: !policyDraft.allow_introspection })}
          />
          <ToggleRow
            icon={Hash} name="Require persisted queries"
            desc="Only allow operations whose SHA-256 hash is in the persisted-query registry. Strongest possible protection — your clients must register every query they ship."
            on={!!policyDraft.require_persisted} disabled={!canConfigure}
            onChange={() => patch({ require_persisted: !policyDraft.require_persisted })}
          />
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.5} sx={{ py: 1.75 }}>
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ fontSize: 14, fontWeight: 600, mb: 0.375 }}>
                <LayerdIcon icon={FileText} size="xs" color="neutral" /> Body cap
              </Stack>
              <Typography sx={GQL_BODY_SX}>
                Reject the request before parsing if the body exceeds this many bytes.
              </Typography>
            </Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <AsecTextField type="number" disabled={!canConfigure} compact
                inputProps={{ min: 1024, max: 10485760, style: { textAlign: "right", fontWeight: 600 } }}
                value={policyDraft.body_size_cap_bytes ?? 65536}
                onChange={(e) => patch({ body_size_cap_bytes: parseInt(e.target.value, 10) || 65536 })}
                sx={{ width: 120 }} />
              <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: colors.neutral[700] }}>bytes</Typography>
            </Stack>
          </Stack>
        </AsecSectionCard>
      </Stack>

      <Stack spacing={2.5} sx={{ position: "sticky", top: 80 }}>
        <ModeHelpCard mode={policyDraft.mode || "off"} />
        <TipCard policy={policyDraft} obs={obs} />
        <Stack direction="row" justifyContent="flex-end">
          <GhostButton
            size="small"
            startIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
            onClick={() => navigate("/v2/api-security/events?category=graphql")}
          >
            Open GraphQL event log
          </GhostButton>
        </Stack>
      </Stack>
    </Box>
  );
}

function parsePersistedQuery(text) {
  if (!text?.trim()) return { kind: null, operationName: null, preview: null };
  const oneLine = text.trim().replace(/\s+/g, " ");
  const kindMatch = oneLine.match(/^(query|mutation|subscription)\b/i);
  const kind = kindMatch ? kindMatch[1].toLowerCase() : null;
  const nameMatch = oneLine.match(/^(?:query|mutation|subscription)\s+([A-Za-z_][A-Za-z0-9_]*)/i);
  const operationName = nameMatch?.[1] || null;
  const preview = oneLine.length > 96 ? `${oneLine.slice(0, 96)}…` : oneLine;
  return { kind, operationName, preview };
}

function persistedKindKey(kind) {
  if (kind === "query") return "q";
  if (kind === "mutation") return "m";
  if (kind === "subscription") return "s";
  return null;
}

function PersistedQueryRow({ item, canDelete, onDelete }) {
  const parsed = parsePersistedQuery(item.query_text);
  const title = item.name || parsed.operationName
    || (item.query_text ? "Unnamed operation" : "Hash-only entry");
  const kindKey = persistedKindKey(parsed.kind);

  return (
    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}
      sx={{ p: "12px 14px", borderRadius: "8px", backgroundColor: colors.neutral[100], border: `1px solid ${colors.neutral[200]}` }}>
      <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
          {kindKey ? <OpKind kind={kindKey} /> : null}
          <AsecEventsBodyText>{title}</AsecEventsBodyText>
        </Stack>
        {item.query_hash ? (
          <Tooltip title={item.query_hash} arrow>
            <AsecMono sx={{ fontSize: 12, color: colors.neutral[700], cursor: "default" }}>
              {item.query_hash.slice(0, 20)}…
            </AsecMono>
          </Tooltip>
        ) : null}
        {parsed.preview ? (
          <Tooltip title={item.query_text} arrow>
            <Typography sx={{ fontSize: 12, color: "#1E40AF", lineHeight: "25px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "default" }}>
              {parsed.preview}
            </Typography>
          </Tooltip>
        ) : null}
        {item.created_at ? (
          <Typography sx={GQL_MUTED_SX}>Registered {formatDate(item.created_at)}</Typography>
        ) : null}
      </Stack>
      {canDelete ? (
        <GhostButton size="small"
          startIcon={<DeleteOutlineIcon sx={{ fontSize: 14, color: colors.red[100] }} />}
          onClick={() => onDelete(item.query_hash)}
          sx={{ color: colors.red[100], minWidth: 0, px: 1, flexShrink: 0 }}>
          Remove
        </GhostButton>
      ) : null}
    </Stack>
  );
}

function OpKind({ kind }) {
  const p = kind === "q" ? { bg: "#E8FBEF", fg: "#0f7a42", lbl: "QUERY" }
    : kind === "m" ? { bg: "#FFF6D0", fg: "#7a5b10", lbl: "MUTATION" }
      : kind === "s" ? { bg: colors.primary[100], fg: colors.primary[500], lbl: "SUB" }
        : { bg: colors.neutral[100], fg: colors.neutral[700], lbl: "—" };
  return (
    <Box sx={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      width: 64, py: 0.375, borderRadius: "5px", fontSize: 12, fontWeight: 700, letterSpacing: "0.04em",
      backgroundColor: p.bg, color: p.fg,
    }}>{p.lbl}</Box>
  );
}

function LimChip({ kind, children }) {
  const p = kind === "danger" ? { bg: "#FEECEE", fg: "#A11228", bd: "#FBC9CF" }
    : kind === "warn" ? { bg: "#FFF6D0", fg: "#7a5b10", bd: "#FFE89A" }
      : { bg: "rgba(99,113,247,0.10)", fg: colors.primary[500], bd: "rgba(99,113,247,0.20)" };
  return (
    <Box component="span" sx={{
      display: "inline-flex", alignItems: "center", gap: 0.625,
      fontSize: 12, fontWeight: 600,
      px: 0.875, py: 0.25, borderRadius: "5px",
      backgroundColor: p.bg, color: p.fg, border: `1px solid ${p.bd}`,
    }}>{children}</Box>
  );
}

function Reason({ kind, icon, children }) {
  const p = kind === "warn" ? { bg: "#FFF6D0", fg: "#7a5b10", bd: "#FFE89A" }
    : { bg: "#FEECEE", fg: "#A11228", bd: "#FBC9CF" };
  return (
    <Box component="span" sx={{
      display: "inline-flex", alignItems: "center", gap: 0.625,
      fontSize: 12, fontWeight: 600, px: 1, py: 0.375, borderRadius: "5px",
      backgroundColor: p.bg, color: p.fg, border: `1px solid ${p.bd}`, whiteSpace: "nowrap",
    }}>
      <LayerdIcon icon={icon} size="xs" />
      {children}
    </Box>
  );
}

function EmptyRow({ colSpan, children }) {
  return (
    <TableRow>
      <TableCell colSpan={colSpan} sx={{ textAlign: "center", py: 4, borderBottom: "none" }}>
        <Typography sx={GQL_MUTED_SX}>{children}</Typography>
      </TableCell>
    </TableRow>
  );
}

function buildOperationsSummaryMetrics(policy, ops, hero) {
  const total = ops.length;
  const anon = ops.filter((o) => o.name === "<anonymous>").length;
  const anonCalls = ops.filter((o) => o.name === "<anonymous>").reduce((s, o) => s + o.calls, 0);
  const overCap = ops.filter((o) => o.status === "over").length;
  const totalCalls = ops.reduce((s, o) => s + o.calls, 0);
  const top = ops[0]; // ops is sorted by calls DESC server-side
  const topPct = totalCalls > 0 && top ? Math.round((top.calls / totalCalls) * 100) : 0;
  const overCapBlockedCalls = ops
    .filter((o) => o.status === "over")
    .reduce((s, o) => s + Math.max(o.blocks || 0, 0), 0);
  return [
    {
      key: "distinct",
      icon: Activity,
      label: "Distinct operations · 24h",
      value: fmt(total),
      sub: total === 0
        ? <Typography sx={GQL_MUTED_SX}>no traffic in window</Typography>
        : <Typography sx={GQL_BODY_SX}>across {fmt(totalCalls)} calls</Typography>,
    },
    {
      key: "anonymous",
      icon: SearchCode,
      label: "Anonymous queries",
      value: fmt(anon),
      unit: total > 0 ? `of ${fmt(total)}` : undefined,
      sub: total === 0
        ? <Typography sx={GQL_MUTED_SX}>—</Typography>
        : <>{Math.round((anon / total) * 100)}% · {fmt(anonCalls)} calls</>,
    },
    {
      key: "top-op",
      icon: Route,
      label: "Top operation",
      value: top ? `${top.name} · ${fmt(top.calls)}` : "—",
      sub: top ? `${topPct}% of all calls` : <Typography sx={GQL_MUTED_SX}>no traffic</Typography>,
    },
    {
      key: "over-cap",
      icon: AlertTriangle,
      label: "Over-cap operations",
      value: fmt(overCap),
      valueSx: overCap > 0 ? { color: "#A11228" } : undefined,
      sub: overCap === 0
        ? <Typography sx={GQL_MUTED_SX}>all within caps</Typography>
        : <>would reject {fmt(overCapBlockedCalls)} calls</>,
    },
  ];
}

function OperationsTab({
  obs, persisted, pq, setPq, addPersisted, delPersisted, canWrite, canDelete, policy,
  bulkPersist, importFromTraffic, importing,
}) {
  const [search, setSearch] = React.useState("");
  const [filter, setFilter] = React.useState("all");
  const [selected, setSelected] = React.useState(() => new Set());
  const [persisting, setPersisting] = React.useState(false);
  const ops = obs?.operations || [];
  const totalOps = ops.length;

  // Hashes already in the persisted registry — used to disable
  // checkboxes for rows the operator has already allowlisted.
  const persistedHashes = React.useMemo(
    () => new Set((persisted || []).map((p) => (p.query_hash || "").toLowerCase())),
    [persisted],
  );
  const isEligibleHash = (h) => !!h && /^[0-9a-f]{16,}$/.test((h || "").toLowerCase());
  const totals = React.useMemo(() => {
    let query = 0, mutation = 0, sub = 0, over = 0;
    for (const o of ops) {
      if (o.kind === "q") query++;
      else if (o.kind === "m") mutation++;
      else if (o.kind === "s") sub++;
      if (o.status === "over") over++;
    }
    return { query, mutation, sub, over };
  }, [ops]);
  const chips = [
    { key: "all", label: "All", ct: totalOps },
    { key: "query", label: "Query", ct: totals.query },
    { key: "mutation", label: "Mutation", ct: totals.mutation },
    { key: "subscription", label: "Subscription", ct: totals.sub },
    { key: "over", label: "Over cap", ct: totals.over },
  ];
  const rows = ops.filter((r) => {
    if (filter !== "all") {
      const match = filter === "query" ? r.kind === "q"
        : filter === "mutation" ? r.kind === "m"
          : filter === "subscription" ? r.kind === "s"
            : filter === "over" ? r.status === "over"
              : true;
      if (!match) return false;
    }
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (r.name || "").toLowerCase().includes(q)
      || String(r.operation_hash || "").toLowerCase().includes(q);
  });

  // Only offer bulk-persist on rows that have a real hash and aren't
  // already registered. Header-checkbox "select all" applies to this
  // eligible subset within the current filter.
  const eligibleRowsInView = rows.filter(
    (r) => isEligibleHash(r.operation_hash) && !persistedHashes.has((r.operation_hash || "").toLowerCase()),
  );
  const allEligibleSelected = eligibleRowsInView.length > 0
    && eligibleRowsInView.every((r) => selected.has((r.operation_hash || "").toLowerCase()));
  const someEligibleSelected = eligibleRowsInView.some((r) => selected.has((r.operation_hash || "").toLowerCase()));
  const toggleAll = () => {
    const next = new Set(selected);
    if (allEligibleSelected) {
      for (const r of eligibleRowsInView) next.delete((r.operation_hash || "").toLowerCase());
    } else {
      for (const r of eligibleRowsInView) next.add((r.operation_hash || "").toLowerCase());
    }
    setSelected(next);
  };
  const toggleOne = (hash) => {
    const h = (hash || "").toLowerCase();
    const next = new Set(selected);
    if (next.has(h)) next.delete(h); else next.add(h);
    setSelected(next);
  };
  const handleBulkPersist = async () => {
    if (!bulkPersist || selected.size === 0) return;
    // Send with operation_name from the matching op row so the
    // registry entry has a human-readable description.
    const hashToName = new Map();
    for (const r of ops) {
      if (r.operation_hash) hashToName.set(r.operation_hash.toLowerCase(), r.name || null);
    }
    const items = Array.from(selected).map((h) => ({ query_hash: h, name: hashToName.get(h) || null }));
    setPersisting(true);
    try {
      await bulkPersist(items);
      setSelected(new Set());
    } finally {
      setPersisting(false);
    }
  };

  return (
    <Stack spacing={2.5}>
      <AsecSectionCard title="Persisted query registry" icon={Hash}
        right={<AsecStatusPill kind={policy?.require_persisted ? "danger" : "warn"}>
          {policy?.require_persisted ? "Enforcement on" : "Enforcement off"} · {persisted.length} registered
        </AsecStatusPill>}
        radius="12px" noPadding bodySx={{ p: 0 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 260px" }}>
          <Box sx={{ p: 2, borderRight: `1px solid ${colors.neutral[200]}` }}>
            <AsecTextField fullWidth multiline minRows={4} sanitize={false} spellCheck={false} disabled={!canWrite} value={pq}
              label="Paste a GraphQL operation to allowlist"
              onChange={(e) => setPq(e.target.value)}
              placeholder={'query getDashboard { viewer { id name } metrics(range: "24h") { revenue orders } }'}
              sx={{ "& .MuiInputBase-input": { fontSize: 12, lineHeight: "18px" } }} />
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1.25, p: "8px 11px", borderRadius: "8px", backgroundColor: colors.neutral[100], border: `1px solid ${colors.neutral[100]}` }}>
              <LayerdIcon icon={Fingerprint} size="xs" color="primary" />
              <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: colors.neutral[700] }}>SHA-256</Typography>
              <Typography sx={{ ml: "auto", ...GQL_BODY_SX }}>computed on the server on paste · added to the allowlist</Typography>
            </Stack>
          </Box>
          <Stack spacing={1.25} sx={{ p: 2 }}>
            <PrimaryButton startIcon={<AddIcon />} onClick={addPersisted}
              disabled={!canWrite || !pq.trim()} sx={{ ...asecFormPrimaryButtonSx, justifyContent: "center" }}>Register</PrimaryButton>
            <GhostButton
              startIcon={importing
                ? <CircularProgress size={14} sx={{ color: "inherit" }} />
                : <LayerdIcon icon={ScanLine} size="sm" color="neutral" />}
              disabled={!canWrite || !importFromTraffic || importing}
              onClick={() => importFromTraffic && importFromTraffic()}
              sx={{ ...asecFormGhostButtonSx, justifyContent: "center" }}
            >
              {importing ? "Importing…" : "Import from traffic"}
            </GhostButton>
            <Box sx={{
              display: "flex", alignItems: "flex-start", gap: 1.5, p: "12px 14px",
              borderRadius: "10px", backgroundColor: "rgba(99,113,247,0.06)",
              border: "1px solid rgba(99,113,247,0.20)",
            }}>
              <Box sx={{
                width: 28, height: 28, borderRadius: "8px", backgroundColor: "#fff",
                border: "1px solid rgba(99,113,247,0.20)", color: colors.primary[300],
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}><LayerdIcon icon={ShieldCheck} size="sm" color="primary" /></Box>
              <Box>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: colors.primary[500], mb: 0.25 }}>Lock the surface</Typography>
                <Typography sx={GQL_BODY_SX}>
                  With <strong>Require persisted</strong> on, only registered hashes run. Register your live operations, then flip enforcement.
                </Typography>
              </Box>
            </Box>
          </Stack>
        </Box>
        {persisted.length > 0 && (
          <Box sx={{ borderTop: `1px solid ${colors.neutral[200]}`, p: "12px 18px" }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: colors.neutral[700], mb: 1 }}>
              Registered ({persisted.length})
            </Typography>
            <Stack spacing={1.25}>
              {persisted.map((p) => (
                <PersistedQueryRow key={p.query_hash} item={p} canDelete={canDelete} onDelete={delPersisted} />
              ))}
            </Stack>
          </Box>
        )}
      </AsecSectionCard>

      <AsecMetricStrip metrics={buildOperationsSummaryMetrics(policy, ops, obs?.hero)} />
      <AsecEventsFilterBar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search operation name or hash…"
        categoryFilter={filter}
        onCategoryChange={setFilter}
        chips={chips}
        action={(
          <GhostButton
            size="small"
            startIcon={persisting
              ? <CircularProgress size={14} sx={{ color: "inherit" }} />
              : <AddIcon sx={{ fontSize: 16 }} />}
            disabled={!canWrite || selected.size === 0 || persisting}
            onClick={handleBulkPersist}
          >
            {persisting ? "Persisting…" : selected.size > 0 ? `Persist ${selected.size} selected` : "Persist selected"}
          </GhostButton>
        )}
      />
      <AsecEventsTableShell
        footer={(
          <AsecEventsTableFooter
            from={rows.length ? 1 : 0}
            to={rows.length}
            total={totalOps}
            label="distinct operations"
          />
        )}
      >
        <TableHead>
          <TableRow>
            <AsecEventsHeadCell align="left" sx={{ width: 44, pl: 1.5 }}>
              <Checkbox
                size="small"
                disabled={!canWrite || eligibleRowsInView.length === 0}
                checked={allEligibleSelected}
                indeterminate={!allEligibleSelected && someEligibleSelected}
                onChange={toggleAll}
                sx={{ p: 0 }}
              />
            </AsecEventsHeadCell>
            {["Type", "Name", "Hash", "Peak used", "24h calls", "Status", "Action"].map((h, i) => (
              <AsecEventsHeadCell key={h} align={i === 4 || i === 6 ? "right" : "left"}>{h}</AsecEventsHeadCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <EmptyRow colSpan={8}>
              {totalOps === 0
                ? "No GraphQL operations observed in the last 24 hours."
                : "No operations match the current filter."}
            </EmptyRow>
          ) : rows.map((r, idx) => {
            const isMut = r.operation_type === "mutation";
            const dCap = isMut ? (policy.mutation_max_depth ?? 5) : (policy.query_max_depth ?? 10);
            const cCap = isMut ? (policy.mutation_max_complexity ?? 500) : (policy.query_max_complexity ?? 1000);
            const aCap = policy.query_max_aliases ?? 15;
            const dangerHas = (k) => (r.danger || []).includes(k);
            const hashLower = (r.operation_hash || "").toLowerCase();
            const alreadyPersisted = persistedHashes.has(hashLower);
            const canSelect = isEligibleHash(r.operation_hash) && !alreadyPersisted && canWrite;
            const rowSelected = selected.has(hashLower);
            return (
              <TableRow key={`${r.operation_type}-${r.name}-${idx}`} sx={asecEventRowSx}>
                <TableCell sx={{ width: 44, pl: 1.5 }}>
                  {alreadyPersisted ? (
                    <Tooltip title="Already in persisted registry" arrow>
                      <CheckCircleOutlineIcon sx={{ fontSize: 18, color: colors.green[300] }} />
                    </Tooltip>
                  ) : (
                    <Checkbox
                      size="small"
                      disabled={!canSelect}
                      checked={rowSelected}
                      onChange={() => toggleOne(hashLower)}
                      sx={{ p: 0 }}
                    />
                  )}
                </TableCell>
                <TableCell><OpKind kind={r.kind} /></TableCell>
                <TableCell>
                  <Stack>
                    <AsecEventsBodyText>{r.name}</AsecEventsBodyText>
                    <AsecEventsMonoLine>
                      {`avg d ${r.avg_depth} · avg c ${r.avg_complexity}${r.blocks ? ` · ${r.blocks} blocks` : ""}`}
                    </AsecEventsMonoLine>
                  </Stack>
                </TableCell>
                <TableCell>
                  <AsecEventsBodyText sx={{ fontSize: 12, color: colors.neutral[700] }}>
                    {r.operation_hash || "—"}
                  </AsecEventsBodyText>
                </TableCell>
                <TableCell>
                  <LimChip kind={dangerHas("depth") ? "danger" : null}>
                    d {r.peak_depth}<span style={{ color: colors.neutral[600], fontWeight: 500, fontSize: 12 }}>/{dCap}</span>
                  </LimChip>{" "}
                  <LimChip kind={dangerHas("cost") ? "warn" : null}>
                    c {r.peak_complexity}<span style={{ color: colors.neutral[600], fontWeight: 500, fontSize: 12 }}>/{cCap >= 1000 ? `${cCap / 1000}k` : cCap}</span>
                  </LimChip>
                  {r.peak_aliases > 0 && (
                    <>{" "}<LimChip kind={dangerHas("aliases") ? "warn" : null}>a {r.peak_aliases}<span style={{ color: colors.neutral[600], fontWeight: 500, fontSize: 12 }}>/{aCap}</span></LimChip></>
                  )}
                </TableCell>
                <TableCell align="right">
                  <AsecEventsBodyText sx={{ color: r.status === "over" ? "#A11228" : undefined }}>
                    {r.calls.toLocaleString()}
                  </AsecEventsBodyText>
                </TableCell>
                <TableCell>
                  {r.status === "over" ? <AsecStatusPill kind="danger">Over cap</AsecStatusPill>
                    : r.status === "review" ? <AsecStatusPill kind="warn">Review</AsecStatusPill>
                      : <AsecStatusPill kind="ok">OK</AsecStatusPill>}
                </TableCell>
                <AsecEventsViewCell />
              </TableRow>
            );
          })}
        </TableBody>
      </AsecEventsTableShell>
    </Stack>
  );
}

function buildAuditSummaryMetrics(audit, hero) {
  const total = audit.length;
  const callers = new Set(audit.map((r) => r.src_ip).filter(Boolean));
  const byReason = new Map();
  for (const r of audit) {
    const k = r.failure_kind || "other";
    byReason.set(k, (byReason.get(k) || 0) + 1);
  }
  let topReason = "-";
  let topReasonCount = 0;
  for (const [k, v] of byReason) {
    if (v > topReasonCount) { topReason = k; topReasonCount = v; }
  }
  return [
    {
      key: "violations",
      icon: AlertTriangle,
      label: "Violations · 24h",
      value: fmt(total),
      sub: (
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
          <AsecStatusPill kind="warn">{fmt(hero?.blocks || 0)} would-block</AsecStatusPill>
        </Stack>
      ),
    },
    {
      key: "top-reason",
      icon: Layers3,
      label: "Top reason",
      value: total === 0 ? "—" : `${topReason} · ${topReasonCount}`,
      sub: total === 0 ? <Typography sx={GQL_MUTED_SX}>no violations in window</Typography>
        : `${Math.round((topReasonCount / total) * 100)}% of violations`,
    },
    {
      key: "callers",
      icon: Users,
      label: "Distinct callers",
      value: fmt(callers.size),
      unit: callers.size ? "IPs" : undefined,
    },
    {
      key: "overhead",
      icon: Gauge,
      label: "Window",
      value: "24 h",
      sub: <Typography sx={GQL_MUTED_SX}>rolling</Typography>,
    },
  ];
}

function AuditTab({ obs }) {
  const [search, setSearch] = React.useState("");
  const [filter, setFilter] = React.useState("all");
  const audit = obs?.audit || [];
  const chipTallies = React.useMemo(() => {
    const t = new Map();
    for (const r of audit) {
      const b = failureBucket(r.failure_kind).key;
      t.set(b, (t.get(b) || 0) + 1);
    }
    return t;
  }, [audit]);
  const chips = [
    { key: "all", label: "All", ct: audit.length },
    ...Array.from(chipTallies.entries()).map(([k, ct]) => ({ key: k, label: k, ct })),
  ];
  const rows = audit.filter((r) => {
    const b = failureBucket(r.failure_kind).key;
    if (filter !== "all" && b !== filter) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (r.operation_name || "").toLowerCase().includes(q)
      || (r.src_ip || "").includes(q)
      || (r.operation_hash || "").toLowerCase().includes(q);
  });
  return (
    <Stack spacing={2.5}>
      <AsecMetricStrip metrics={buildAuditSummaryMetrics(audit, obs?.hero)} />
      <AsecEventsFilterBar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search operation, caller IP, or hash…"
        categoryFilter={filter}
        onCategoryChange={setFilter}
        chips={chips}
        action={(
          <GhostButton
            size="small"
            startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
            disabled={rows.length === 0}
            onClick={() => downloadAuditCsv(rows)}
          >
            Export CSV
          </GhostButton>
        )}
      />
      <AsecEventsTableShell
        footer={(
          <AsecEventsTableFooter
            from={rows.length ? 1 : 0}
            to={rows.length}
            total={audit.length}
            label="violations"
          />
        )}
      >
        <TableHead>
          <TableRow>
            {["Time", "Type", "Operation", "Caller", "Reason", "Cost", "Action"].map((h, i) => (
              <AsecEventsHeadCell key={h} align={i === 5 || i === 6 ? "right" : "left"}>{h}</AsecEventsHeadCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <EmptyRow colSpan={7}>
              {audit.length === 0
                ? "No GraphQL violations in the last 24 hours."
                : "No violations match the current filter."}
            </EmptyRow>
          ) : rows.map((r, idx) => {
            const bucket = failureBucket(r.failure_kind);
            const reasonText = r.apollo_code || r.failure_kind || "violation";
            return (
              <TableRow key={r.ts || idx} sx={asecEventRowSx}>
                <TableCell>
                  <Stack>
                    <AsecEventsBodyText sx={{ fontSize: 12 }}>{timeOfDay(r.ts)}</AsecEventsBodyText>
                    <Typography sx={GQL_MUTED_SX}>{timeSince(r.ts)}</Typography>
                  </Stack>
                </TableCell>
                <TableCell><OpKind kind={OP_KIND_FROM_TYPE(r.operation_type)} /></TableCell>
                <TableCell>
                  <Stack>
                    <AsecEventsBodyText>{r.operation_name || "<anonymous>"}</AsecEventsBodyText>
                    <AsecEventsMonoLine truncate={48}>
                      {`${r.path || ""}${r.operation_hash ? ` · ${r.operation_hash}` : ""}`}
                    </AsecEventsMonoLine>
                  </Stack>
                </TableCell>
                <TableCell>
                  <AsecEventsBodyText sx={{ fontSize: 12 }}>{r.src_ip || "—"}</AsecEventsBodyText>
                </TableCell>
                <TableCell>
                  <Reason kind={bucket.tone} icon={bucket.icon}>{reasonText}</Reason>
                </TableCell>
                <TableCell align="right">
                  <AsecEventsBodyText>
                    {r.complexity ? r.complexity.toLocaleString() : "—"}
                  </AsecEventsBodyText>
                </TableCell>
                <AsecEventsViewCell />
              </TableRow>
            );
          })}
        </TableBody>
      </AsecEventsTableShell>
    </Stack>
  );
}

function Body({ siteId, onGoToPolicy }) {
  const { showSuccess, showError } = useNotification();
  const { canConfigure, canWrite, canDelete } = useAsecPermissions();
  const [tab, setTab] = React.useState(0);
  const [policyBaseline, setPolicyBaseline] = React.useState(null);
  const [policyDraft, setPolicyDraft] = React.useState(null);
  const [persisted, setPersisted] = React.useState([]);
  const [obs, setObs] = React.useState(null);
  const [obsFetchedAt, setObsFetchedAt] = React.useState(null);
  const [pq, setPq] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const tabKeys = ["policy", "operations", "audit"];

  const reload = React.useCallback(async () => {
    if (!siteId) return;
    const [pr, persistedRes, obsRes] = await Promise.all([
      asec.gqlPolicy(siteId).catch(() => ({ data: null })),
      asec.gqlPersistedList(siteId).catch(() => ({ data: [] })),
      asec.gqlObservability(siteId, { range: "24h" }).catch(() => ({ data: null })),
    ]);
    const next = pr.data || { mode: "off" };
    setPolicyBaseline(next);
    setPolicyDraft(next);
    setPersisted(persistedRes.data || []);
    setObs(obsRes.data || null);
    setObsFetchedAt(new Date());
  }, [siteId]);
  React.useEffect(() => { reload(); }, [reload]);

  React.useEffect(() => {
    if (onGoToPolicy) onGoToPolicy(() => setTab(0));
  }, [onGoToPolicy]);

  const patch = React.useCallback((p) => {
    if (!canConfigure) return;
    setPolicyDraft((prev) => ({ ...prev, ...p }));
  }, [canConfigure]);

  const modifiedFields = React.useMemo(
    () => collectObjectDiff(policyBaseline, policyDraft, GQL_POLICY_DIFF_FIELDS),
    [policyBaseline, policyDraft],
  );
  const modifiedCount = modifiedFields.length;

  const discard = () => setPolicyDraft(policyBaseline);
  const apply = async () => {
    if (!siteId || !policyDraft || !canConfigure) return;
    setSaving(true);
    try {
      // Strip fields not accepted by the backend schema (e.g. site_id, id, timestamps).
      const allowedKeys = new Set(GQL_POLICY_DIFF_FIELDS.map((f) => f.key));
      const payload = Object.fromEntries(
        Object.entries(policyDraft).filter(([k]) => allowedKeys.has(k))
      );
      await asec.gqlPolicySet(siteId, payload);
      showSuccess("Policy saved");
      await reload();
    } catch (e) {
      showError(e?.message || String(e));
    } finally {
      setSaving(false);
    }
  };
  const addPersisted = async () => {
    if (!canWrite || !pq.trim()) return;
    try { await asec.gqlPersistedCreate(siteId, { query: pq }); setPq(""); showSuccess("Registered"); reload(); }
    catch (e) { showError(e?.message || String(e)); }
  };
  const delPersisted = async (hash) => {
    if (!canDelete) return;
    try { await asec.gqlPersistedDelete(siteId, hash); reload(); }
    catch (e) { showError(e?.message || String(e)); }
  };
  const bulkPersist = async (items) => {
    if (!canWrite || !items || items.length === 0) return;
    try {
      const r = await asec.gqlPersistedBulk(siteId, { items });
      const d = r?.data || {};
      const added = Number(d.added || 0);
      const updated = Number(d.updated || 0);
      showSuccess(
        added && updated ? `Registered ${added} · updated ${updated}`
        : added ? `Registered ${added} operation${added === 1 ? "" : "s"}`
        : updated ? `Updated ${updated} operation${updated === 1 ? "" : "s"}`
        : "No changes"
      );
      reload();
    } catch (e) {
      showError(e?.response?.data?.message || e?.message || String(e));
    }
  };
  const [importing, setImporting] = React.useState(false);
  const importFromTraffic = async () => {
    if (!canWrite) return;
    setImporting(true);
    try {
      const r = await asec.gqlPersistedImportFromTraffic(siteId, {
        range: "24h", limit: 50, include_anonymous: false, only_successful: true,
      });
      const d = r?.data || {};
      const added = Number(d.added || 0);
      const skipped = Number(d.skipped_already_persisted || 0);
      const considered = Number(d.considered || 0);
      if (added === 0 && considered === 0) {
        showSuccess("No candidate named operations in the last 24h");
      } else if (added === 0) {
        showSuccess(`No new operations to import (${skipped} already registered)`);
      } else {
        showSuccess(`Imported ${added} operation${added === 1 ? "" : "s"} from traffic${skipped ? ` · skipped ${skipped} already registered` : ""}`);
      }
      reload();
    } catch (e) {
      showError(e?.response?.data?.message || e?.message || String(e));
    } finally {
      setImporting(false);
    }
  };

  if (policyDraft === null) {
    return (
      <Box sx={{ py: 5, textAlign: "center", color: colors.neutral[700] }}>
        <CircularProgress size={24} sx={{ mb: 2 }} />
        <Typography>Loading GraphQL policy…</Typography>
      </Box>
    );
  }

  const opsCount = obs?.operations?.length ?? 0;
  const auditCount = obs?.audit?.length ?? 0;

  return (
    <Stack spacing={2.5} sx={GQL_PAGE_TEXT_SX}>
      <AsecMetricStrip metrics={buildHeroMetrics(policyDraft, obs)} />

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={asecTabsSx}>
        <Tab label="Policy" icon={<LayerdIcon icon={SlidersHorizontal} size="sm" color="inherit" />} iconPosition="start" />
        <Tab label={`Operations (${opsCount})`} icon={<LayerdIcon icon={Binary} size="sm" color="inherit" />} iconPosition="start" />
        <Tab label={`Audit log (${auditCount})`} icon={<LayerdIcon icon={List} size="sm" color="inherit" />} iconPosition="start" />
      </Tabs>

      <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1} sx={{ mt: -1 }}>
        <Box sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: colors.green[300] }} />
        <Typography sx={GQL_CAPTION_SX}>
          {obs ? `Live · last sync ${obsFetchedAt ? timeSince(obsFetchedAt.toISOString()) : "just now"}` : "No live data"}
        </Typography>
      </Stack>

      {tabKeys[tab] === "policy" && (
        <PolicyTab policyDraft={policyDraft} patch={patch} canConfigure={canConfigure} obs={obs} />
      )}
      {tabKeys[tab] === "operations" && (
        <OperationsTab
          obs={obs}
          policy={policyDraft}
          persisted={persisted} pq={pq} setPq={setPq}
          addPersisted={addPersisted} delPersisted={delPersisted}
          bulkPersist={bulkPersist}
          importFromTraffic={importFromTraffic}
          importing={importing}
          canWrite={canWrite} canDelete={canDelete} />
      )}
      {tabKeys[tab] === "audit" && <AuditTab obs={obs} />}

      {tabKeys[tab] === "policy" && (
        <StickySaveBar
          open={modifiedCount > 0 && canConfigure}
          modifiedCount={modifiedCount}
          modifiedFields={modifiedFields}
          onDiscard={discard}
          onApply={apply}
          isApplying={saving}
        />
      )}
    </Stack>
  );
}

// Modal that lets an operator paste a GraphQL query and see whether
// it would pass or be rejected by the site's current GraphQL policy.
// Hits POST /asec/graphql/policy/test — the backend mirrors the data
// plane's depth/complexity/alias/root-field/introspection scoring so
// verdicts match what would actually happen in enforce mode.
function TestQueryModal({ open, onClose, siteId }) {
  const { showError } = useNotification();
  const queryInputRef = React.useRef(null);
  const [query, setQuery] = React.useState(
    "# Paste your GraphQL query here\n" +
    "query getUserOrders($first: Int = 10) {\n" +
    "  user(id: \"u_1\") {\n" +
    "    id name email\n" +
    "    orders(first: $first) { id total currency }\n" +
    "  }\n" +
    "}\n"
  );
  const [operationName, setOperationName] = React.useState("");
  const [running, setRunning] = React.useState(false);
  const [result, setResult] = React.useState(null);

  React.useEffect(() => {
    if (!open) return undefined;
    const timer = window.setTimeout(() => {
      const input = queryInputRef.current;
      if (!input) return;
      input.focus();
      const end = input.value?.length ?? 0;
      input.setSelectionRange(end, end);
    }, 50);
    return () => window.clearTimeout(timer);
  }, [open]);

  const run = async () => {
    if (!siteId || !query.trim()) return;
    setRunning(true);
    setResult(null);
    try {
      const r = await asec.gqlPolicyTest(siteId, {
        query,
        operation_name: operationName || undefined,
      });
      setResult(r.data);
    } catch (e) {
      const msg = (e && e.response && e.response.data && (e.response.data.message || e.response.data.error))
        || (e && e.message)
        || "Test failed";
      showError(msg);
    } finally {
      setRunning(false);
    }
  };

  const handleClose = () => {
    setResult(null);
    onClose();
  };

  return (
    <Dialog
      open={open} onClose={handleClose} maxWidth="lg" fullWidth
      PaperProps={{ sx: { borderRadius: "12px", overflow: "hidden", maxHeight: "min(90vh, 860px)", backgroundColor: "#fff" } }}
    >
      <DialogTitle sx={{
        display: "flex", alignItems: "flex-start", justifyContent: "space-between",
        gap: 1.5, px: 3, py: 2.25, backgroundColor: "#fff", borderBottom: `1px solid ${colors.neutral[200]}`,
      }}>
        <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ minWidth: 0 }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: "10px", display: "flex",
            alignItems: "center", justifyContent: "center", flexShrink: 0, backgroundColor: "#EEF0FE",
          }}>
            <LayerdIcon icon={Terminal} size="sm" color="primary" />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography component="span" sx={{ ...typography.h2, color: colors.primary[300] }}>
              Test a query against the current policy
            </Typography>
            <Typography sx={{ ...GQL_BODY_SX, mt: 0.5, maxWidth: 560 }}>
              Paste a GraphQL document to preview how the live policy would score depth, complexity, and other limits.
            </Typography>
          </Box>
        </Stack>
        <IconButton onClick={handleClose} size="small" sx={asecIconButtonSx} aria-label="Close">
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column", backgroundColor: "#fff" }}>
        <Box sx={{
          display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.35fr 1fr" }, gap: 0, flex: 1, minHeight: { md: 480 },
        }}>
          {/* LEFT: query editor */}
          <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2.5, backgroundColor: "#fff" }}>
            <AsecField label="GraphQL query" helper="Supports queries, mutations, and subscriptions. Variables are not evaluated in this preview.">
              <Box sx={{ border: `1px solid ${colors.neutral[200]}`, borderRadius: "8px", overflow: "hidden", backgroundColor: "#fff" }}>
                <Box sx={{
                  px: 1.5, py: 1, borderBottom: `1px solid ${colors.neutral[200]}`, backgroundColor: "#fff",
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 600, color: colors.neutral[600], textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    Query document
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: colors.neutral[500], fontFamily: "monospace" }}>GraphQL</Typography>
                </Box>
                <AsecTextField
                  fullWidth multiline minRows={14} maxRows={22} spellCheck={false} sanitize={false} autoFocus
                  inputRef={queryInputRef} value={query} onChange={(e) => setQuery(e.target.value)}
                  inputProps={{ style: { fontFamily: "ui-monospace, monospace", fontSize: 12.5, lineHeight: "20px" } }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 0, backgroundColor: "#fff", alignItems: "flex-start",
                      "& fieldset": { border: "none" },
                      "&.Mui-focused": { boxShadow: `inset 0 0 0 1px ${colors.primary[300]}` },
                    },
                    "& .MuiOutlinedInput-input": { py: 1.5, px: 1.75 },
                  }}
                />
              </Box>
            </AsecField>

            <AsecField label="Operation name" optHint="(optional)" helper="Only needed when the document defines more than one operation.">
              <AsecTextField
                fullWidth sanitize={false} value={operationName} onChange={(e) => setOperationName(e.target.value)}
                placeholder="e.g. getUserOrders"
                inputProps={{ style: { fontFamily: "ui-monospace, monospace", fontSize: 12.5 } }}
              />
            </AsecField>

            <Stack direction="row" justifyContent="flex-end" sx={{ pt: 0.5 }}>
              <PrimaryButton
                onClick={run} disabled={running || !query.trim()}
                startIcon={running ? <CircularProgress size={14} sx={{ color: "inherit" }} /> : <LayerdIcon icon={Play} size="sm" color="inherit" />}
              >
                {running ? "Running…" : "Run test"}
              </PrimaryButton>
            </Stack>
          </Box>

          {/* RIGHT: verdict panel */}
          <Box sx={{
            p: 3, backgroundColor: "#fff",
            borderLeft: { md: `1px solid ${colors.neutral[200]}` },
            borderTop: { xs: `1px solid ${colors.neutral[200]}`, md: "none" },
            display: "flex", flexDirection: "column", minHeight: { xs: 280, md: "auto" },
          }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <Box sx={{
                width: 28, height: 28, borderRadius: "8px", display: "flex",
                alignItems: "center", justifyContent: "center", backgroundColor: "#fff", border: `1px solid ${colors.neutral[200]}`,
              }}>
                <LayerdIcon icon={ShieldCheck} size="sm" color="primary" />
              </Box>
              <Typography sx={{ ...asecFieldLabelSx, mb: 0 }}>Verdict</Typography>
            </Stack>

            <Box sx={{
              flex: 1, borderRadius: "8px", border: `1px solid ${colors.neutral[200]}`, backgroundColor: "#fff",
              p: 2.5, display: "flex", flexDirection: "column",
              justifyContent: !result && !running ? "center" : "flex-start",
              minHeight: 360,
            }}>
              {!result && !running && (
                <Stack alignItems="center" spacing={1.5} sx={{ textAlign: "center", px: 2 }}>
                  <Box sx={{
                    width: 48, height: 48, borderRadius: "12px", display: "flex",
                    alignItems: "center", justifyContent: "center", backgroundColor: "#EEF0FE",
                  }}>
                    <LayerdIcon icon={SearchCode} size="md" color="primary" />
                  </Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: colors.neutral[1000] }}>No verdict yet</Typography>
                  <Typography sx={{ ...GQL_MUTED_SX, maxWidth: 320 }}>
                    Paste a query and click <strong>Run test</strong> to see whether the current policy would allow or reject it.
                  </Typography>
                </Stack>
              )}
              {running && (
                <Stack spacing={2} alignItems="center" justifyContent="center" sx={{ flex: 1, py: 4 }}>
                  <CircularProgress size={22} sx={{ color: colors.primary[300] }} />
                  <Typography sx={{ fontSize: 13, color: colors.neutral[700] }}>Scoring the query against policy limits…</Typography>
                </Stack>
              )}
              {result && <TestResultPanel result={result} />}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ ...asecDialogActionsSx, justifyContent: "flex-end", backgroundColor: "#fff" }}>
        <GhostButton onClick={handleClose}>Close</GhostButton>
      </DialogActions>
    </Dialog>
  );
}

function TestResultPanel({ result }) {
  const pass = result.verdict === "would_pass";
  const parseError = result.parse_error === true;
  const tone = pass ? "ok" : "danger";
  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <AsecStatusPill kind={tone}>{pass ? "would pass" : "would reject"}</AsecStatusPill>
        {result.mode && (
          <Typography sx={{ fontSize: 12, color: colors.neutral[700] }}>
            Current mode: <strong>{result.mode}</strong>
          </Typography>
        )}
        {result.operation && (
          <Typography sx={{ fontSize: 12, color: colors.neutral[700] }}>
            Operation: <strong>{result.operation.type}{result.operation.name ? ` ${result.operation.name}` : ""}</strong>
          </Typography>
        )}
      </Stack>

      {parseError && (
        <Alert severity="error" sx={{ fontSize: 13 }}>
          <strong>Query did not parse.</strong>
          {result.fail_reasons && result.fail_reasons.length > 0 && (
            <Box component="div" sx={{ fontFamily: "ui-monospace, monospace", fontSize: 12, mt: 0.5 }}>{result.fail_reasons[0]}</Box>
          )}
        </Alert>
      )}

      {!parseError && result.metrics && (
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 1.25 }}>
          <MetricRow label="Depth" value={result.metrics.depth}
            limit={result.controls && result.controls.depth && result.controls.depth.limit}
            pass={result.controls && result.controls.depth && result.controls.depth.pass} />
          <MetricRow label="Complexity" value={result.metrics.complexity}
            limit={result.controls && result.controls.complexity && result.controls.complexity.limit}
            pass={result.controls && result.controls.complexity && result.controls.complexity.pass} />
          <MetricRow label="Aliases" value={result.metrics.alias_count}
            limit={result.controls && result.controls.aliases && result.controls.aliases.limit}
            pass={result.controls && result.controls.aliases && result.controls.aliases.pass} />
          <MetricRow label="Root fields" value={result.metrics.root_field_count}
            limit={result.controls && result.controls.root_fields && result.controls.root_fields.limit}
            pass={result.controls && result.controls.root_fields && result.controls.root_fields.pass} />
          <MetricRow label="Introspection" value={result.metrics.has_introspection ? "present" : "absent"}
            pass={result.controls && result.controls.introspection && result.controls.introspection.pass} />
          {result.controls && result.controls.body_size && (
            <MetricRow label="Body size" value={`${result.controls.body_size.value} B`}
              limit={`${result.controls.body_size.limit} B`} pass={result.controls.body_size.pass} />
          )}
          {result.controls && result.controls.persisted_allowlist && (
            <MetricRow label="Persisted allowlist"
              value={result.controls.persisted_allowlist.pass ? "in allowlist" : "not in allowlist"}
              pass={result.controls.persisted_allowlist.pass} />
          )}
          {result.controls && result.controls.operation_type && (
            <MetricRow label="Operation type"
              value={result.controls.operation_type.value || "(none)"} pass={result.controls.operation_type.pass} />
          )}
        </Box>
      )}

      {!parseError && result.fail_reasons && result.fail_reasons.length > 0 && (
        <Box sx={{ p: 1.5, borderRadius: 1, backgroundColor: "#FFF6E6", border: "1px solid #FFE3B3" }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#8a4a00", mb: 0.5 }}>Why it would be rejected</Typography>
          <Box component="ul" sx={{ pl: 2.25, mb: 0 }}>
            {result.fail_reasons.map((r, i) => (
              <li key={i}><Typography sx={{ fontSize: 12.5, color: colors.neutral[800] }}>{r}</Typography></li>
            ))}
          </Box>
        </Box>
      )}

      {result.query_hash && (
        <Typography sx={{ fontSize: 11, color: colors.neutral[600], fontFamily: "ui-monospace, monospace" }}>
          query SHA-256: {result.query_hash}
        </Typography>
      )}
    </Stack>
  );
}

function MetricRow({ label, value, limit, pass }) {
  const tone = pass === true ? colors.green[300] : pass === false ? colors.red[100] : colors.neutral[800];
  return (
    <Box sx={{
      p: 1.25, borderRadius: "8px", border: `1px solid ${colors.neutral[200]}`,
      backgroundColor: pass === true ? colors.green[10] : pass === false ? "#FFF5F5" : "#fff",
      display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1,
    }}>
      <Typography sx={{ fontSize: 12, color: colors.neutral[700] }}>{label}</Typography>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: tone, fontVariantNumeric: "tabular-nums" }}>{value}</Typography>
        {limit !== undefined && limit !== null && (
          <Typography sx={{ fontSize: 11, color: colors.neutral[600], fontVariantNumeric: "tabular-nums" }}>/ {limit}</Typography>
        )}
      </Stack>
    </Box>
  );
}

// Bridges siteId from the AsecPage children-render scope into the
// page-header "Test query" button (which lives outside the render
// scope). The ref is updated by a tiny SiteIdBridge child so we
// never call setState during render.
function SiteIdBridge({ siteId, onChange }) {
  React.useEffect(() => { onChange(siteId); }, [siteId, onChange]);
  return null;
}

export default function AsecGraphql() {
  const [refreshKey, setRefreshKey] = React.useState(0);
  const [testModalOpen, setTestModalOpen] = React.useState(false);
  const [activeSiteId, setActiveSiteId] = React.useState(null);

  return (
    <AsecPage
      featureKey="api.graphql"
      helpTooltip="GraphQL hardening — depth, complexity, alias and root-field caps, mutation hardening, introspection blocking, persisted-query enforcement. Subscriptions rejected wholesale."
      title="GraphQL hardening"
      helmetTitle="API Security — GraphQL"
      pageDescription="Reject expensive or malicious GraphQL operations before they hit your origin — depth, complexity, alias and root-field caps, body size and persisted-query enforcement."
      onRefresh={() => setRefreshKey((k) => k + 1)}
      headerActions={(
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <PrimaryButton
            startIcon={<LayerdIcon icon={Play} size="sm" color="inherit" />}
            onClick={() => setTestModalOpen(true)}
            disabled={!activeSiteId}
          >
            Test query
          </PrimaryButton>
        </Stack>
      )}
    >
      {(siteId) => (
        <>
          <SiteIdBridge siteId={siteId} onChange={setActiveSiteId} />
          <Body key={refreshKey} siteId={siteId} />
          <TestQueryModal
            open={testModalOpen}
            onClose={() => setTestModalOpen(false)}
            siteId={siteId}
          />
        </>
      )}
    </AsecPage>
  );
}
